// External libraries
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

// Internal imports
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLocation } from "wouter";

// Type imports
import type { VoterWithVerifications } from "@shared/schema";

export default function VoterDetails() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();

  const { data: voter, isLoading, error } = useQuery<VoterWithVerifications>({
    queryKey: ["/api/voters/" + id],
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading voter details...</p>
        </div>
      </div>
    );
  }

  if (error || !voter) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-destructive mb-4">Voter Not Found</h1>
              <p className="text-muted-foreground mb-4">
                The voter you're looking for could not be found.
              </p>
              <Button onClick={() => setLocation("/")} data-testid="back-to-dashboard">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setLocation("/")}
              className="text-primary-foreground hover:bg-primary-foreground hover:bg-opacity-20"
              data-testid="back-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <h1 className="text-xl font-bold" data-testid="page-title">Voter Details</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <Card>
            <CardHeader>
              <CardTitle>Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Voter ID:</span>
                <span className="font-mono" data-testid="voter-id">{voter.voterId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Name:</span>
                <span data-testid="voter-name">{voter.name}</span>
              </div>
              {voter.fatherName && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Father's Name:</span>
                  <span data-testid="father-name">{voter.fatherName}</span>
                </div>
              )}
              {voter.age && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Age:</span>
                  <span data-testid="voter-age">{voter.age}</span>
                </div>
              )}
              {voter.gender && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gender:</span>
                  <span data-testid="voter-gender">{voter.gender}</span>
                </div>
              )}
              {voter.phone && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Phone:</span>
                  <span data-testid="voter-phone">{voter.phone}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Address Information */}
          <Card>
            <CardHeader>
              <CardTitle>Address Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <span className="text-muted-foreground">Address:</span>
                <p className="mt-1" data-testid="voter-address">{voter.address}</p>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Constituency:</span>
                <span data-testid="voter-constituency">{voter.constituency}</span>
              </div>
              {voter.pollingStation && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Polling Station:</span>
                  <span data-testid="polling-station">{voter.pollingStation}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Verification History */}
        {voter.verifications && voter.verifications.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Verification History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {voter.verifications.map((verification) => (
                  <div
                    key={verification.id}
                    className="border rounded-lg p-4"
                    data-testid={`verification-${verification.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-1 text-xs rounded-full status-${verification.status}`}>
                        {verification.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(verification.verifiedAt).toLocaleString()}
                      </span>
                    </div>
                    {verification.notes && (
                      <p className="text-sm text-muted-foreground mt-2" data-testid="verification-notes">
                        {verification.notes}
                      </p>
                    )}
                    {verification.verifiedBy && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Verified by: <span data-testid="verified-by">{verification.verifiedBy}</span>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
